import React, { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { staffAPI } from "../services/api";
import { handleApiError } from "../utils/errorUtils";
import { useCompletedTimers } from "../context/CompletedTimersContext";
import { useFocusedPoll } from "../hooks/useFocusedPoll";
import StaffCheckIn from "./StaffCheckIn";
import StartSessionButton from "./staff/StartSessionButton";
import CheckInAndStartButton from "./staff/CheckInAndStartButton";
import StaffLayout from "./staff/StaffLayout";
import CustomerNameLink from "./staff/CustomerNameLink";
import {
  getSessionDisplayName,
  isMassageSessionType,
} from "../utils/sessionTypeUtils";
import "react-datepicker/dist/react-datepicker.css";
import "./StaffDashboard.css";
import "./staff/StaffActionButtons.css";
import moment from "moment-timezone";

// STAFF-ADDON-VISIBILITY-001 — defensive invariant warn (ADR-042 D1):
// addGuest and doubleOccupancy are mutually exclusive at the BE policy
// layer. If a booking carries BOTH, log a console warning for staff
// visibility (R-024-class signal) and let the badge logic render the
// safe $25 Guest fallback. Returns null so it composes cleanly inside
// JSX as a ternary branch.
const warnDoubleOccupancyAndGuest = (booking) => {
  // eslint-disable-next-line no-console
  console.warn(
    "[STAFF-ADDON-VISIBILITY-001] Booking has both addGuest and doubleOccupancy — ADR-042 D1 invariant violation",
    { bookingId: booking && booking._id }
  );
  return null;
};

const StaffDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "all"); // e.g., "all", "booked", etc.
  const [startDateFilter, setStartDateFilter] = useState(() => {
    const startDate = searchParams.get("startDate");
    return startDate ? moment.tz(startDate, "YYYY-MM-DD", "America/Phoenix").startOf("day").toDate() : null;
  });
  const [endDateFilter, setEndDateFilter] = useState(() => {
    const endDate = searchParams.get("endDate");
    return endDate ? moment.tz(endDate, "YYYY-MM-DD", "America/Phoenix").endOf("day").toDate() : null;
  });
  const token = localStorage.getItem("jwtToken");
  const [showSuiteCalendars, setShowSuiteCalendars] = useState(false);

  // Access the completed timers context
  const { completedTimers } = useCompletedTimers();

  const suites = [
    { suiteType: "massage", suiteNumber: 1 },
    { suiteType: "sauna", suiteNumber: 2 },
    { suiteType: "sauna", suiteNumber: 3 },
    { suiteType: "sauna", suiteNumber: 4 },
    { suiteType: "sauna", suiteNumber: 5 },
    { suiteType: "sauna", suiteNumber: 6 },
    { suiteType: "sauna", suiteNumber: 7 },
    { suiteType: "sauna", suiteNumber: 8 },
    { suiteType: "redlight", suiteNumber: 9 },
    { suiteType: "redlight", suiteNumber: 10 },
  ];

  const currentDashboardUrl = `${location.pathname}${location.search}`;

  // Function to fetch bookings. The `silent` flag is set true by the
  // Tier 1 focus/poll refetch so the booking table doesn't flicker on
  // background refresh (only mount + manual refresh shows the spinner).
  const fetchBookings = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await staffAPI.getBookings();
      setBookings(response.data.bookings);
    } catch (error) {
      handleApiError(
        error,
        "StaffDashboard",
        (message) => console.error(message),
        "Error fetching bookings"
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [token, fetchBookings]);

  // Tier 1 sync posture: refetch when the staff returns to this tab,
  // the window regains focus, or the network reconnects; plus a 30s
  // visibility-aware poll so the day-view stays fresh while in view.
  const silentRefetchBookings = useCallback(
    () => fetchBookings({ silent: true }),
    [fetchBookings]
  );
  useFocusedPoll(silentRefetchBookings, 30000, [silentRefetchBookings]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (activeTab && activeTab !== "all") {
      nextParams.set("tab", activeTab);
    }

    if (startDateFilter) {
      nextParams.set(
        "startDate",
        moment(startDateFilter).tz("America/Phoenix").format("YYYY-MM-DD")
      );
    }

    if (endDateFilter) {
      nextParams.set(
        "endDate",
        moment(endDateFilter).tz("America/Phoenix").format("YYYY-MM-DD")
      );
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    activeTab,
    endDateFilter,
    searchParams,
    setSearchParams,
    startDateFilter,
  ]);

  // Helper to format suite assignment.
  const formatSuiteAssignment = (assignment) => {
    if (!assignment) return "Not Assigned";
    if (assignment.type === "redlight") {
      return `Suite ${assignment.number}`;
    } else if (assignment.type === "sauna") {
      return assignment.handicap
        ? `ADA ${assignment.number}`
        : `Suite ${assignment.number}`;
    } else if (assignment.type === "massage") {
      return `Massage ${assignment.number}`;
    }
    return "Not Assigned";
  };

  const renderFundingLabel = (booking) => {
    if (booking.sessionSourceType === "organization-sponsored") {
      return (
        <div className="staff-funding-cell">
          <span className="staff-funding-badge staff-funding-sponsored">
            {booking.sourceDescription || "Corporate Sponsored"}
          </span>
        </div>
      );
    }

    if (booking.sessionSourceType === "organization-corporate-rate") {
      return (
        <div className="staff-funding-cell">
          <span className="staff-funding-badge staff-funding-corporate">
            {booking.sourceDescription || "Corporate Rate"}
          </span>
          <span className="staff-funding-amount">
            ${Number(booking.organizationRateApplied || 0).toFixed(2)}
          </span>
        </div>
      );
    }

    if (booking.sourceDescription) {
      return (
        <div className="staff-funding-cell">
          <span className="staff-funding-badge staff-funding-standard">
            {booking.sourceDescription}
          </span>
        </div>
      );
    }

    if (booking.sessionSourceType === "paid-service" && isMassageSessionType(booking.sessionType)) {
      return (
        <div className="staff-funding-cell">
          <span className="staff-funding-badge staff-funding-standard">
            Paid Service
          </span>
          {booking.paymentId?.amount ? (
            <span className="staff-funding-amount">
              ${(Number(booking.paymentId.amount) / 100).toFixed(2)}
            </span>
          ) : null}
        </div>
      );
    }

    return <span className="staff-funding-text">Standard</span>;
  };

  // Handler for applying cancellation fee.
  const handleApplyCancellationFee = async (bookingId) => {
    try {
      const response = await staffAPI.applyCancellationFee(bookingId);
      alert(response.data.message);
      fetchBookings();
    } catch (error) {
      handleApiError(
        error,
        "StaffDashboard.applyCancellationFee",
        (message) => alert(message),
        "Error applying cancellation fee."
      );
    }
  };

  const handleMarkComplete = async (bookingId) => {
    if (
      !window.confirm("Are you sure you want to mark this session as complete?")
    )
      return;
    try {
      const response = await staffAPI.completeBooking(bookingId);
      alert(response.data.message);
      fetchBookings();
    } catch (error) {
      handleApiError(
        error,
        "StaffDashboard.markComplete",
        (message) => alert(message),
        "Error marking booking complete."
      );
    }
  };

  // Filter bookings by date using Phoenix local time
  const filterBookingsByDate = (bookingsList) => {
    return bookingsList.filter((booking) => {
      // Convert appointment date to Phoenix time zone
      const apptPhoenix = moment.tz(booking.appointmentDate, "America/Phoenix");

      if (startDateFilter && endDateFilter) {
        // Convert filter dates to moment objects in Phoenix time zone for comparison
        const startMoment = moment(startDateFilter);
        const endMoment = moment(endDateFilter);
        return (
          apptPhoenix.isSameOrAfter(startMoment) &&
          apptPhoenix.isSameOrBefore(endMoment)
        );
      }
      if (startDateFilter) {
        const startMoment = moment(startDateFilter);
        return apptPhoenix.isSameOrAfter(startMoment);
      }
      if (endDateFilter) {
        const endMoment = moment(endDateFilter);
        return apptPhoenix.isSameOrBefore(endMoment);
      }
      return true;
    });
  };

  const filteredBookings =
    activeTab === "all"
      ? filterBookingsByDate(bookings)
      : filterBookingsByDate(
          bookings.filter((b) => b.status.toLowerCase().trim() === activeTab)
        );

  if (loading) {
    return <p>Loading bookings...</p>;
  }

  return (
    <StaffLayout>
      <div className="staff-dashboard">
        <div className="dashboard-header">
          <h1>Staff Dashboard</h1>
        </div>
        {/* Date Range Filter */}
        <div className="date-range-filter">
          <label>
            Start Date:{" "}
            <DatePicker
              selected={startDateFilter}
              onChange={(date) => {
                // Set to start of day in Phoenix time zone
                if (date) {
                  const phoenixDate = moment
                    .tz(date, "America/Phoenix")
                    .startOf("day")
                    .toDate();
                  setStartDateFilter(phoenixDate);
                } else {
                  setStartDateFilter(null);
                }
              }}
              placeholderText="Select start date"
              dateFormat="MMMM d, yyyy"
              timeZone="America/Phoenix"
            />
          </label>
          <label>
            End Date:{" "}
            <DatePicker
              selected={endDateFilter}
              onChange={(date) => {
                // Set to end of day in Phoenix time zone
                if (date) {
                  const phoenixDate = moment
                    .tz(date, "America/Phoenix")
                    .endOf("day")
                    .toDate();
                  setEndDateFilter(phoenixDate);
                } else {
                  setEndDateFilter(null);
                }
              }}
              placeholderText="Select end date"
              dateFormat="MMMM d, yyyy"
              timeZone="America/Phoenix"
            />
          </label>
          <button
            className="action-btn action-btn-secondary"
            onClick={fetchBookings}
          >
            Reset Filters
          </button>
        </div>

        {/* Modern Tab Navigation */}
        <div className="tab-navigation">
          {[
            "all",
            "booked",
            "checked-in",
            "in-progress",
            "completed",
            "cancelled",
          ].map((tab) => (
            <button
              key={tab}
              className={`tab-button ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Bookings Table with Card Header */}
        <div className="content-card">
          <div className="card-header">
            <h2>
              {activeTab === "all"
                ? "All Sessions"
                : `${
                    activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
                  } Sessions`}
            </h2>
            <div className="card-actions">
              <button className="refresh-button" onClick={fetchBookings}>
                Refresh
              </button>
            </div>
          </div>

          {filteredBookings.length > 0 ? (
            <div className="table-responsive">
              <table className="booking-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Member</th>
                    <th>Session Type</th>
                    <th>Funding</th>
                    <th>Extras</th>
                    <th>Status</th>
                    <th>Suite#</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => {
                    const appointment = new Date(booking.appointmentDate);
                    const dateString = appointment.toLocaleDateString("en-US");
                    const timeString = appointment.toLocaleTimeString("en-US", {
                      timeZone: "America/Phoenix",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                    const bookingStatus = booking.status.toLowerCase().trim();

                    let statusTimestamp = "";
                    if (bookingStatus === "booked" && booking.createdAt) {
                      statusTimestamp = new Date(
                        booking.createdAt
                      ).toLocaleTimeString("en-US", {
                        timeZone: "America/Phoenix",
                        month: "numeric",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    } else if (
                      bookingStatus === "checked-in" &&
                      booking.checkedInAt
                    ) {
                      statusTimestamp = new Date(
                        booking.checkedInAt
                      ).toLocaleTimeString("en-US", {
                        timeZone: "America/Phoenix",
                        month: "numeric",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    } else if (
                      bookingStatus === "in-progress" &&
                      booking.startedAt
                    ) {
                      statusTimestamp = new Date(
                        booking.startedAt
                      ).toLocaleTimeString("en-US", {
                        timeZone: "America/Phoenix",
                        month: "numeric",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    } else if (
                      bookingStatus === "completed" &&
                      booking.completedAt
                    ) {
                      statusTimestamp = new Date(
                        booking.completedAt
                      ).toLocaleTimeString("en-US", {
                        timeZone: "America/Phoenix",
                        month: "numeric",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    } else if (
                      bookingStatus === "cancelled" &&
                      booking.cancelledAt
                    ) {
                      statusTimestamp = new Date(
                        booking.cancelledAt
                      ).toLocaleTimeString("en-US", {
                        timeZone: "America/Phoenix",
                        month: "numeric",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    }
                    // Check if this booking has payment-due add-ons
                    const hasPaymentDue =
                      (booking.guestPaymentRequired ||
                        booking.halotherapyPaymentRequired) &&
                      booking.addOnPaymentStatus !== "paid";

                    return (
                      <tr
                        key={booking._id}
                        className={hasPaymentDue ? "payment-required-row" : ""}
                      >
                        <td>{dateString}</td>
                        <td>{timeString}</td>
                        <td>
                          <CustomerNameLink customerId={booking.user?._id}>
                            {booking.user?.preferredName
                              ? booking.user.preferredName
                              : booking.user?.firstName && booking.user?.lastName
                              ? `${booking.user.firstName} ${booking.user.lastName}`
                              : booking.user?.username || "Unknown User"}
                          </CustomerNameLink>
                        </td>

                        <td>
                          {getSessionDisplayName(booking.sessionType)}
                        </td>
                        <td>{renderFundingLabel(booking)}</td>
                        <td>
                          {booking.sessionType === "infrared" ? (
                            <>
                              {/* Defensive: ADR-042 D1 says addGuest and
                                  doubleOccupancy are mutually exclusive at
                                  the BE policy layer. Warn on the
                                  invariant violation (R-024-class signal);
                                  the badge below still renders the safe
                                  $25 Guest fallback in that case. */}
                              {booking.addGuest && booking.doubleOccupancy
                                ? warnDoubleOccupancyAndGuest(booking)
                                : null}
                              {(booking.addGuest || booking.doubleOccupancy) && (
                                <span
                                  className={`addon-indicator ${
                                    booking.doubleOccupancy && !booking.addGuest
                                      ? "entitlement-used"
                                    : booking.guestPaymentRequired &&
                                      booking.addOnPaymentStatus !== "paid"
                                      ? "payment-required"
                                    : booking.usedGuestPass
                                      ? "entitlement-used"
                                      : booking.addOnPaymentStatus === "paid"
                                        ? "entitlement-used"
                                        : "payment-required"
                                  }`}
                                >
                                  {booking.doubleOccupancy && !booking.addGuest
                                    ? "Guest (Corp)"
                                    : "Guest"}
                                  {booking.addGuest &&
                                    booking.guestPaymentRequired &&
                                    ` ($${booking.guestAddOnFee})`}
                                  {booking.addGuest &&
                                    booking.guestPaymentRequired &&
                                    booking.addOnPaymentStatus === "paid" &&
                                    " (Paid)"}
                                  {booking.addGuest &&
                                    booking.usedGuestPass &&
                                    " (Pass)"}
                                </span>
                              )}
                              {(booking.addGuest || booking.doubleOccupancy) &&
                                (booking.aromatherapy ||
                                  booking.halotherapy) && <span>, </span>}
                              {booking.aromatherapy && (
                                <span className="addon-indicator standard">
                                  Aromatherapy
                                </span>
                              )}
                              {booking.aromatherapy && booking.halotherapy && (
                                <span>, </span>
                              )}
                              {booking.halotherapy && (
                                <span
                                  className={`addon-indicator ${
                                    booking.halotherapyPaymentRequired &&
                                    booking.addOnPaymentStatus !== "paid"
                                      ? "payment-required"
                                    : booking.usedHalotherapyEntitlement
                                      ? "entitlement-used"
                                      : booking.addOnPaymentStatus === "paid"
                                        ? "entitlement-used"
                                        : "payment-required"
                                  }`}
                                >
                                  Halotherapy
                                  {booking.halotherapyPaymentRequired &&
                                    ` ($${booking.halotherapyAddOnFee})`}
                                  {booking.halotherapyPaymentRequired &&
                                    booking.addOnPaymentStatus === "paid" &&
                                    " (Paid)"}
                                  {booking.usedHalotherapyEntitlement &&
                                    " (Ent)"}
                                </span>
                              )}
                            </>
                          ) : isMassageSessionType(booking.sessionType) ? (
                            "Massage"
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          <div className="status-with-payment">
                            <span
                              className={`status-badge status-${bookingStatus}`}
                            >
                              {booking.status}
                            </span>
                            {hasPaymentDue && (
                              <span className="payment-alert-badge">
                                <span className="payment-alert-icon">💳</span>
                                Payment Due
                              </span>
                            )}
                          </div>
                          {statusTimestamp && (
                            <span className="status-timestamp">
                              {" "}
                              {statusTimestamp}
                            </span>
                          )}
                        </td>
                        <td>
                          {formatSuiteAssignment(booking.suiteAssignment)}
                        </td>
                        <td className="actions-cell">
                          {bookingStatus === "completed" ? null : booking.status
                              .toLowerCase()
                              .trim() === "cancelled" &&
                            booking.cancellationFeeEligible &&
                            !booking.cancellationFeeApplied ? (
                            <button
                              className="action-btn action-btn-danger"
                              onClick={() =>
                                handleApplyCancellationFee(booking._id)
                              }
                            >
                              Apply Fee
                            </button>
                          ) : (
                            booking.status.toLowerCase().trim() !==
                              "cancelled" && (
                              <div className="action-buttons">
                                <Link
                                  to={`/staff/edit-booking/${booking._id}`}
                                  state={{ returnTo: currentDashboardUrl }}
                                >
                                  <button className="action-btn action-btn-primary">
                                    Edit
                                  </button>
                                </Link>
                                {bookingStatus === "in-progress" && (
                                  <button
                                    className="action-btn action-btn-success"
                                    onClick={() =>
                                      handleMarkComplete(booking._id)
                                    }
                                  >
                                    Complete
                                  </button>
                                )}
                                {bookingStatus === "checked-in" && (
                                  <StartSessionButton
                                    bookingId={booking._id}
                                    sessionType={booking.sessionType}
                                    onSessionStarted={fetchBookings}
                                  />
                                )}
                                {bookingStatus === "booked" && (
                                  <CheckInAndStartButton
                                    bookingId={booking._id}
                                    appointmentDate={booking.appointmentDate}
                                    refreshBookings={fetchBookings}
                                  />
                                )}
                                {bookingStatus === "booked" && (
                                  <StaffCheckIn
                                    bookingId={booking._id}
                                    appointmentDate={booking.appointmentDate}
                                    refreshBookings={fetchBookings}
                                  />
                                )}
                              </div>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No bookings available for this tab.</p>
            </div>
          )}
        </div>
        {/* Create Member Modal */}
      </div>
    </StaffLayout>
  );
};

export default StaffDashboard;
